import { jsxs, jsx } from "react/jsx-runtime";
import { useCallback } from "react";
import { cn } from "../../../lib/utils.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import { QuizResultFeedback } from "../shared/quiz-result-feedback.js";
import { SubmitButton } from "../shared/submit-button.js";
import { useTranslation } from "react-i18next";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import { QuizMedia } from "../shared/quiz-media.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { DifficultyBadge, DebugQuizId } from "../shared/debug-quiz-id.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useOrderingQuiz } from "../hooks/use-ordering-quiz.js";
import { useSensors, useSensor, DndContext, closestCenter, KeyboardSensor, PointerSensor } from "../../../node_modules/.pnpm/@dnd-kit_core@6.3.1_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/@dnd-kit/core/dist/core.esm.js";
import { arrayMove, SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, sortableKeyboardCoordinates, useSortable } from "../../../node_modules/.pnpm/@dnd-kit_sortable@8.0.0_@dnd-kit_core@6.3.1_react-dom@18.3.1_react@18.3.1__react@18.3.1__react@18.3.1/node_modules/@dnd-kit/sortable/dist/sortable.esm.js";
import { CSS } from "../../../node_modules/.pnpm/@dnd-kit_utilities@3.2.2_react@18.3.1/node_modules/@dnd-kit/utilities/dist/utilities.esm.js";
import { motion } from "../../../node_modules/.pnpm/framer-motion@11.18.2_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/framer-motion/dist/es/render/components/motion/proxy.js";
import ArrowRight from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/arrow-right.js";
const restrictToHorizontal = ({ transform }) => ({
  ...transform,
  y: 0
});
const restrictToVertical = ({ transform }) => ({
  ...transform,
  x: 0
});
function SortableItem({ id, answer, index, isCorrect, isSolved }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 99 : "auto"
  };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: setNodeRef,
      style,
      className: cn(
        "group relative flex items-center justify-center w-full max-w-[400px] h-[80px] mx-auto md:h-[176px] px-4 py-3 border-2 rounded-3xl bg-white transition-all",
        "[box-shadow:0px_4px_0px_0px_rgba(0,0,0,0.10)]",
        isDragging && "border-[#55C3FF] bg-[#EEF9FF] text-[#55C3FF] [box-shadow:0px_8px_16px_0px_rgba(0,0,0,0.20)] z-10",
        !isDragging && !isSolved && "border-[#BBBBBB] text-[#333] hover:border-[#55C3FF] hover:text-[#55C3FF] hover:[box-shadow:0px_4px_0px_0px_rgba(85,195,255,0.30)]",
        isSolved && isCorrect === true && "border-[#3BD0AE] bg-[#E2F8F3] text-[#17AF8C] [box-shadow:unset]",
        isSolved && isCorrect === false && "border-[#FF675E] bg-[#FFF0EF] text-[#FF675E] [box-shadow:unset]",
        isSolved && "pointer-events-none"
      ),
      ...attributes,
      ...listeners,
      children: [
        /* @__PURE__ */ jsx("div", { className: cn(
          "absolute left-1/2 -translate-x-1/2 -top-4 flex items-center justify-center rounded-full font-bold transition-all",
          isDragging && "w-20 h-8 bg-[#55C3FF] text-white border-none",
          !isDragging && !isSolved && "w-8 h-8 border-2 border-[#BBBBBB] bg-[#f5f5f5] text-[#999] group-hover:bg-[#DDF3FF] group-hover:border-[#55C3FF] group-hover:text-[#55C3FF]",
          isSolved && isCorrect === true && "w-8 h-8 bg-[#3BD0AE] text-white border-none",
          isSolved && isCorrect === false && "w-8 h-8 bg-[#FF675E] text-white border-none"
        ), children: index + 1 }),
        answer.image && /* @__PURE__ */ jsx(
          "img",
          {
            src: answer.image.signedUrl || answer.image.url,
            alt: `item-${index}`,
            className: "max-h-24 w-auto object-contain"
          }
        ),
        answer.answer && /* @__PURE__ */ jsx("span", { className: "text-[20px] font-medium text-center", children: /* @__PURE__ */ jsx(KatexText, { text: answer.answer }) })
      ]
    }
  );
}
function OrderingStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel
}) {
  const { t } = useTranslation("quiz");
  const {
    orderedChoices,
    setOrderedChoices,
    isVertical,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSubmit
  } = useOrderingQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrderedChoices((prev) => {
      const oldIndex = prev.findIndex((c) => c.key === active.id);
      const newIndex = prev.findIndex((c) => c.key === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, [setOrderedChoices]);
  const displayChoices = orderedChoices;
  const getArrowColor = () => {
    if (!isSolved) return "text-[#55C3FF]";
    if ((solveResult == null ? void 0 : solveResult.correct) || shouldShowCorrectAnswers) return "text-[#3BD0AE]";
    return "text-[#FF675E]";
  };
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        className: "space-y-2",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "inline-flex gap-2 items-center", children: [
            /* @__PURE__ */ jsx("div", { className: "inline-flex gap-2 items-center px-3 py-1 text-sm font-bold text-white bg-[#55C3FF] rounded-lg", children: /* @__PURE__ */ jsx("span", { children: t("types.ordering.label") }) }),
            /* @__PURE__ */ jsx(DifficultyBadge, { level: difficultyLevel }),
            /* @__PURE__ */ jsx(DebugQuizId, { quizId })
          ] }),
          /* @__PURE__ */ jsx("h3", { className: "text-2xl font-bold leading-relaxed text-gray-900", children: /* @__PURE__ */ jsx(KatexText, { text: quiz.title }) }),
          quiz.problem && /* @__PURE__ */ jsx("div", { className: "text-xl leading-relaxed text-gray-900", children: formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? void 0) }),
          /* @__PURE__ */ jsx(QuizMedia, { image: quiz.image, imageMobile: quiz.imageMobile, desmos: quiz.desmos, vars: quiz.vars })
        ]
      }
    ),
    !isSolved && !isViewSolved && /* @__PURE__ */ jsx("p", { className: "text-base text-gray-500", children: t("types.ordering.dragInstruction") }),
    /* @__PURE__ */ jsx(
      DndContext,
      {
        sensors,
        collisionDetection: closestCenter,
        onDragEnd: handleDragEnd,
        modifiers: isVertical ? [restrictToVertical] : [restrictToHorizontal],
        children: /* @__PURE__ */ jsx(
          SortableContext,
          {
            items: displayChoices.map((c) => c.key),
            strategy: isVertical ? verticalListSortingStrategy : horizontalListSortingStrategy,
            children: /* @__PURE__ */ jsx("div", { className: cn(
              isVertical ? "flex flex-col items-center justify-start space-y-14 p-2" : "flex flex-row items-center justify-between space-x-8 p-2"
            ), children: displayChoices.map((choice, index) => /* @__PURE__ */ jsxs(
              "div",
              {
                className: "relative",
                style: !isVertical ? { width: `${100 / displayChoices.length}%` } : void 0,
                children: [
                  index > 0 && /* @__PURE__ */ jsx(
                    ArrowRight,
                    {
                      strokeWidth: 3,
                      className: cn(
                        "absolute w-6 h-6",
                        getArrowColor(),
                        isVertical ? "left-1/2 -translate-x-1/2 -top-9 rotate-90" : "-left-5 top-1/2 -translate-y-1/2"
                      )
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    SortableItem,
                    {
                      id: choice.key,
                      answer: choice,
                      index,
                      isCorrect: isSolved ? shouldShowCorrectAnswers || ((solveResult == null ? void 0 : solveResult.correct) ?? false) : null,
                      isSolved
                    }
                  )
                ]
              },
              choice.key
            )) })
          }
        )
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel ?? t("common.checkAnswer")
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizResultFeedback,
      {
        isCorrect: (solveResult == null ? void 0 : solveResult.correct) ?? false,
        show: isSolved,
        correctAnswer: shouldShowCorrectAnswers && (solveResult == null ? void 0 : solveResult.correctAnswers) ? solveResult.correctAnswers.map((key) => {
          var _a;
          return (_a = quiz.choices.find((c) => c.key === key)) == null ? void 0 : _a.answer;
        }).filter(Boolean).join(" → ") : void 0,
        disableSound
      }
    ),
    isSolved && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: !(solveResult == null ? void 0 : solveResult.correct),
        actionLabel: isShowingCorrectAnswers ? t("common.viewMyAnswer") : t("common.viewCorrectAnswer"),
        onActionClick: () => setIsShowingCorrectAnswers((v) => !v)
      }
    )
  ] });
}
export {
  OrderingStudent
};
//# sourceMappingURL=ordering-student.js.map
